import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { SegmentButton, SegmentButtonGroup } from '../../../../components/common/buttons/SegmentButton';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import {
  WHEEL_CONDITION_OPTIONS,
  WheelConditionLevel,
} from '../../../../constants/openMarketProduct/openMarketProduct';
import { sellTractorPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { sellTractorSurveySteps } from '../../../../constants/survey/sellTractorSurvey';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { SellTractorSurveyPageWrapper } from '../SellTractorSurveyPageWrapper';

const WHEEL_LABELS = {
  tractor: {
    headerTitle: '타이어 상태를 알려주세요.',
    pair1: '앞 타이어',
    pair2: '뒷 타이어',
  },
  combine: {
    headerTitle: '궤도바퀴 상태를 알려주세요.',
    pair1: '좌 궤도바퀴',
    pair2: '우 궤도바퀴',
  },
  transplanter: {
    headerTitle: '바퀴 상태를 알려주세요.',
    pair1: '앞 바퀴',
    pair2: '뒷 바퀴',
  },
} as const;

export const SellTractorSurveyTireConditionPage = () => {
  const navigate = useNavigate();
  const [pair1TireCondition, setPair1TireCondition] = useState<WheelConditionLevel | null>();
  const [pair2TireCondition, setPair2TireCondition] = useState<WheelConditionLevel | null>();

  const dispatch = useAppDispatch();
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;
  const userSellTractorSurveySelector = useAppSelector((state) => state.userSellTractorSurvey);
  const machineType = userSellTractorSurveySelector.machineType || 'tractor';
  const labels = WHEEL_LABELS[machineType];

  const currentStep = sellTractorSurveySteps.STEP5;
  const steps = Object.keys(sellTractorSurveySteps);

  const handlePair1TireConditionToogle = (condition: WheelConditionLevel) => {
    if (pair1TireCondition === condition) {
      setPair1TireCondition(null);
    } else {
      setPair1TireCondition(condition);
    }
  };

  const handlePair2TireConditionToogle = (condition: WheelConditionLevel) => {
    if (pair2TireCondition === condition) {
      setPair2TireCondition(null);
    } else {
      setPair2TireCondition(condition);
    }
  };

  useEffect(() => {
    const {
      frontTireCondition,
      rearTireCondition,
      leftTrackCondition,
      rightTrackCondition,
      frontWheelCondition,
      rearWheelCondition,
    } = userSellTractorSurveySelector;
    if (machineType === 'combine') {
      setPair1TireCondition(leftTrackCondition);
      setPair2TireCondition(rightTrackCondition);
    } else if (machineType === 'transplanter') {
      setPair1TireCondition(frontWheelCondition);
      setPair2TireCondition(rearWheelCondition);
    } else {
      setPair1TireCondition(frontTireCondition);
      setPair2TireCondition(rearTireCondition);
    }
  }, [userSellTractorSurveySelector, machineType]);

  const dispatchSellTractorInputValuesToStore = () => {
    const actions = {
      combine: {
        pair1: userSellTractorSurveyActions.changeLeftTrackCondition,
        pair2: userSellTractorSurveyActions.changeRightTrackCondition,
      },
      tractor: {
        pair1: userSellTractorSurveyActions.changeFrontTireCondition,
        pair2: userSellTractorSurveyActions.changeRearTireCondition,
      },
      transplanter: {
        pair1: userSellTractorSurveyActions.changeFrontWheelCondition,
        pair2: userSellTractorSurveyActions.changeRearWheelCondition,
      },
    };

    const { pair1, pair2 } = actions[machineType];

    if (pair1TireCondition) {
      dispatch(pair1(pair1TireCondition));
    }
    if (pair2TireCondition) {
      dispatch(pair2(pair2TireCondition));
    }
  };

  return (
    <SellTractorSurveyPageWrapper
      headerTitle={labels.headerTitle}
      canConfirm
      confirmHandler={() => {
        dispatchSellTractorInputValuesToStore();
        navigate(sellTractorPageInfo.extraInfo2Page.path, { replace: true });
      }}
      closeHandler={dispatchSellTractorInputValuesToStore}
      prevButtonHandler={() => {
        navigate(sellTractorPageInfo.extraInfo1Page.path, { replace: true });
      }}
      steps={steps}
      currentStep={currentStep}
    >
      <SurveyForm padding={'0px 16px'} gap={0}>
        <SurveyInputSection>
          <SurveyInputHeader inputTitle={labels.pair1} isOptional />

          <SegmentButtonGroup>
            {Object.entries(WHEEL_CONDITION_OPTIONS).map(([key, condition]) => (
              <SegmentButton
                key={`front-${key}`}
                isSelected={pair1TireCondition === condition}
                onClick={() => handlePair1TireConditionToogle(condition)}
              >
                {condition}
              </SegmentButton>
            ))}
          </SegmentButtonGroup>
        </SurveyInputSection>

        <SurveyInputSection>
          <SurveyInputHeader inputTitle={labels.pair2} isOptional />
          <SegmentButtonGroup>
            {Object.entries(WHEEL_CONDITION_OPTIONS).map(([key, condition]) => (
              <SegmentButton
                key={`rear-${key}`}
                isSelected={pair2TireCondition === condition}
                onClick={() => handlePair2TireConditionToogle(condition)}
              >
                {condition}
              </SegmentButton>
            ))}
          </SegmentButtonGroup>
        </SurveyInputSection>
      </SurveyForm>
    </SellTractorSurveyPageWrapper>
  );
};

const SurveyInputSection = styled.div`
  display: flex;
  padding-bottom: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;
